import { css, html, nothing, PropertyValueMap } from 'lit';
import { connect } from 'pwa-helpers';
import {
  AddressBook,
  AddressType,
  OrderHistory as IOrderHistory,
  ProductType,
  UserOrderDetails,
} from './order-history.interface';
import { fetchOrderHistory } from './order-history.thunks';
import { BaseComponent } from '../../features/base-component/base-component';
import store, { waitForUser } from '../../store/store';
import { UserProfile } from '../user-profile/user-profile.interface';
import { fetchUserProfile } from '../user-profile/user-profile.thunks';
import { StoreState } from '../../store/store.interface';
import { formatDateDefault, formattedAuthors, ordinalSuffixOf, PROFESSIONAL } from '../../common/utils/util';
import { getCartItemFormat, getPriceWithCurrency, isHEInstructor } from '../../common/utils/cart-util';
import {
  appendDurationForTunnelPurchaseFlow,
  appendedDurationForHEProducts,
  calculateDurationDigitalProduct,
  getImageSrcUrl,
  isSharpenPlus,
} from '../../common/utils/product-util';
import { loading } from '../../common/constants/api-constants';
import { ActionStatus } from '../../common/constants/action-status';
import environment from '../../common/environment';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { getFa4AppUrl, getItemLaunchObjectUrl } from '../../common/constants/professional-redirects';
import emit from '../../common/events';
import { getAnalyticsSafeData, selectUser } from '../user-profile/user-profile.selectors';
import { ItemType } from '../product-card/product.interface';
import { customElement, state } from 'lit/decorators.js';
import { loadCustomer } from '../customer/customer';
import { isInactiveFreeTrial } from '../free-trial/utils/free-trial.util';
import { getOrderHistoryData, getOrderHistoryStatus } from './order-history.selectors';
import {
  getPaymentMethod,
  getOrderUiStatus,
  isHEOrder,
  getBadgeStyleFromLineStatusItem,
  getOrderProgramIds,
  determinePaymentType,
} from './utils/order-history.util';
import { getAppFlow, getUserActiveTab } from '../user-profile/utils/user-profile.util';
import { SSOUtils } from '../sso/utils/sso.utils';
import { SSOToWindow } from '../sso/browse-context/direction/to-window/sso-to-window.service';
import { checkForRedirectToLogin } from '../../common/utils/login-utils';
import { getOrderTypeFromOrderDetails, getProductTypeBU, getSharpenAnalyticsOrderDetails } from '../../common/utils/analytics-util';
import { boardsBeyondCTA } from '../firstaid-forward/utils/boards-beyond.util';
import { getReturnEligibleThrough } from '../return/utils/return.util';
import { MessageType } from '../alert/global-message.interface';
import {
  getLaunchSharpenUrl,
  getSharpenBundleLaunchURL,
  getSharpenCompanionTitle,
  isSharpenBundleOrder,
  isSharpenUpsellPresentInOrder,
} from '../sharpen/utils/sharpen.util';
import { SHARPEN_PROGRAM_ID } from '../upsell/constants/sharpen-page-type.constants';
import { isMhpEbook } from '../mhp/utils/mhp.util';
import { OfferTypeChecker } from '../../common/utils/offer-type-util';
import { getClinicalReasoningLaunchCta } from '../firstaid-forward/utils/clinical-reasoning.util';
import { getAccessType, renderAccessMessage } from '../../common/utils/access-message-util';
import { getMyBookshelfLaunchCta } from '../my-bookshelf/utils/my-bookshelf.util';

const eBookPurchaseBranding = '30DTSEBKP';
const eBookRentalBranding = '30DTSEBK';
const productSourceCreate = 'CREATE';

@customElement('mhecomm-order-history')
export class OrderHistory extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .loading-container {
        display: grid;
        grid-template-columns: 2fr 8fr 2fr;
        gap: 10px;
      }
    `,
  ];

  @state()
  protected user: UserProfile | undefined;

  @state()
  protected ordersHistory: IOrderHistory[] | undefined;

  private collapses = {};

  @state()
  protected loading = true;

  private _analyticsEmitted = false;

  constructor() {
    super();
    const ssoUtils = new SSOUtils();
    new SSOToWindow(ssoUtils).exchangeTokenWhenReceiveReadySignal('boardsbeyond', '/dashboard');
  }

  async connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
    store.dispatch(loadCustomer());
    waitForUser().then(() => {
      this.fetchOrders();
    });
  }

  protected fetchOrders(forceUpdate = false) {
    store.dispatch(fetchOrderHistory({ currentPage: '1', ...(forceUpdate && { forceUpdate: true }) }));
  }

  protected loadOrderHistory() {
    this._analyticsEmitted = false;
    this.fetchOrders(true);
  }

  protected getOrdersHistoryData(state: StoreState) {
    return getOrderHistoryData(state);
  }

  protected getOrdersHistoryStatus(state: StoreState) {
    return getOrderHistoryStatus(state);
  }

  stateChanged(state: StoreState) {
    checkForRedirectToLogin(state.userProfileState.userProfileResponse);
    this.user = selectUser(state);

    const orderHistroyData = this.getOrdersHistoryData(state);
    const orderHistoryStatus = this.getOrdersHistoryStatus(state);

    if (orderHistroyData) {
      this.ordersHistory = this._denormalizeOrderHistory(orderHistroyData);
      this.loading = false;
      if (!this._analyticsEmitted) {
        this._analyticsEmitted = true;
        this._orderHistoryAnalyticsData(this.ordersHistory);
      }
    } else if (orderHistoryStatus === ActionStatus.failed) {
      this.loading = false;
    } else {
      this.loading = true;
    }
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(_changedProperties);
    if (this.ordersHistory) {
      this.ordersHistory!.forEach((order) => {
        const collapseId = `#orderHistoryCollapse${order.webOrderNo}`;

        this.collapses[collapseId] = this.bsInit(collapseId, window.bootstrap?.Collapse, {
          toggle: false,
        })?.instance;
      });
    }
  }

  renderComp() {
    if (this._isLoading()) {
      return this._renderLoadingTemplate();
    } else if (this.ordersHistory) {
      return this.renderContent();
    } else {
      return html`<p>Please sign in to see your order history</p>`;
    }
  }

  renderContent() {
    const totalOrders = this.ordersHistory!.length;
    const descriptionHowManyOrders = totalOrders > 1 ? 'orders' : 'order';

    return totalOrders === 0
      ? html`<p class="fs-3">
          You haven't placed any orders yet with this account
        </p>`
      : html`
          <div class="row justify-content-sm-center">
            <div class="col-12">
              <div class="fw-light">
                <p>${totalOrders} ${descriptionHowManyOrders} found.</p>
              </div>
              <mhecomm-alert
                type="${MessageType.warning}"
                ?dismissible="${false}"
                message="Please note: If your order contains multiple items, the entire order must be returned in a single request."
              ></mhecomm-alert>
              ${this.ordersHistory?.map(
        (order, index) => html`
                  <div class="card text-left rounded-3 ${index > 0 ? 'mt-5' : ''}">
                    <div class="card-header py-3">
                      <div class="lh-1">
                        <div class="row">
                          <div class="col">
                            <h2 class="h4 fs-large fs-sm-4 mb-1">
                              Order Placed: ${formatDateDefault(order.orderedDate)}
                            </h2>
                          </div>
                          <div class="col">
                            <div class="fs-small my-1 text-end">
                              <span class="fw-bold">Return Eligible Through:</span>
                              ${formatDateDefault(getReturnEligibleThrough(order))}
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col">
                            <div class="lh-base fs-small fs-sm-6">
                              <span>Order Number:</span>
                              <span class="fw-bold" data-automation-id="order_${order.webOrderNo}">
                                ${order.webOrderNo}
                              </span>
                              &nbsp;|&nbsp; Total:&nbsp;
                              <span class="fw-bold"
                                >${getPriceWithCurrency(order.currencyCode, order.totalPrice)}
                                (${order.userOrderDetails.length}
                                ${order.userOrderDetails.length > 1 ? 'items' : 'item'})</span
                              >
                            </div>
                          </div>
                          <div class="col">
                            <div class="my-1 text-end">
                              <mhecomm-order-return 
                                .order=${order}
                                @mhecomm-order-returned="${() => this.loadOrderHistory()}"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="card-body p-0">
                      <ul class="list-group">
                        ${order.userOrderDetails.map(
          (item) => OfferTypeChecker.isCreateCustom(item.offerType)
            ? html`
                <li class="d-block">
                  <mhecomm-create-custom-order-item-display
                    .cta="${this._triggerCTAAnalyticsData.bind(this)}"
                    .item="${{...item, productDescription: this.getTitle(item)}}"
                    .order="${order}"
                  >
                  </mhecomm-create-custom-order-item-display>
                </li>`
            : html`
                            <li class="d-block">
                              <div class="row p-3">
                                <div class="col-4 col-sm-3 col-md-2">
                                  <img
                                    alt=""
                                    class="img-fluid"
                                    src=${getImageSrcUrl(item.coverImage ? item.coverImage : item.productIsbn)}
                                  />
                                </div>
                                <div class="col-8 col-sm-9 col-md-4">
                                  <h3 class="h4 fs-large fw-semibold mb-0">
                                    <span data-automation-id="item-title-${order.webOrderNo}-${item.isbn10}"
                                      >${this.getTitle(item)}</span
                                    >
                                  </h3>
                                  ${!item.freeTrialInfo?.isFreeTrial
              ? html`
                                        <div class="mt-2 mb-2">
                                          <span
                                            class="badge ${getBadgeStyleFromLineStatusItem(order, item)}"
                                            data-automation-id="item-orderstatus-${order.webOrderNo}-${item.isbn10}">
                                            ${getOrderUiStatus(order, item)}
                                          </span>
                                        </div>
                                      `
              : nothing}
                                  ${item.freeTrialInfo?.isFreeTrial
              ? html` <mhecomm-free-trial-end-date .freeTrial="${item.freeTrialInfo}" /> `
              : nothing}
                                  ${!isSharpenPlus(item.programSourceId)
              ? html`
                                        <div class="d-none d-sm-block">
                                          <span class="fw-bold"
                                            >${item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'}</span
                                          >
                                          ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
                                          ${item.isbn13
                  ? html`
                                                |
                                                <span class="fw-bold">ISBN13:</span>
                                                ${item.isbn13}
                                              `
                  : ''}
                                        </div>
                                        <div class="d-sm-none">
                                          <span class="fw-bold"
                                            >${item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'}</span
                                          >
                                          ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
                                          ${item.isbn13 ? '|' : ''}
                                        </div>
                                        ${item.isbn13
                  ? html`
                                              <div class="d-sm-none">
                                                <span class="fw-bold">
                                                  ISBN13:
                                                </span>
                                                ${item.isbn13}
                                              </div>
                                            `
                  : ''}
                                      `
              : nothing}
                                  ${!isSharpenPlus(item.programSourceId) &&
              item.authors &&
              !item.freeTrialInfo?.isFreeTrial
              ? html` <div>
                                        <span class="fw-bold">By:</span>
                                        ${formattedAuthors(item.authors)}
                                      </div>`
              : ''}
                                  ${item.grades
              ? html` <div>
                                        <span class="fw-bold">Grades:</span>
                                        ${item.grades}
                                      </div>`
              : ''}
                                  ${!isSharpenPlus(item.programSourceId) && item.copyrightYear && !item.isEvergreen
              ? html` <div>©${item.copyrightYear}</div> `
              : ''}
                                  ${item.formatDescription
              ? html`
                                        <div>
                                          <span class="fw-bold">Format:</span>
                                          ${getCartItemFormat(item.formatDescription, item.formatCode, item.virtual)}
                                        </div>
                                      `
              : ''}
                                  ${isSharpenPlus(item.programSourceId)
              ? html` <div class="mt-2 mt-md-3 badge bg-info text-black">
                                        Expires on
                                        ${calculateDurationDigitalProduct(
                Number(item.extension_attributes?.duration),
                item.extension_attributes?.duration_unit,
                item.createTime
              ).toLocaleDateString('en-US')}
                                      </div>`
              : nothing}
                                </div>
                                <div class="col-12 col-sm-12 col-md-3">
                                  <hr class="hr-primary d-md-none" aria-hidden="true" />
                                  <div class="row">
                                    <div class="col-8 col-sm-8 col-md-12 col-lg-12">
                                      <div class="row pb-3">
                                       ${item.isDiscountedBundle ? html`
                                        <div class="d-flex justify-content-start justify-content-md-end mb-0">
                                          <span>Original Price:&nbsp;</span>
                                          <span class="fw-semibold fs-large text-decoration-line-through">
                                            ${getPriceWithCurrency(order.currencyCode, item.originalPrice * item.itemQuantity)}
                                          </span>
                                        </div>` : nothing
            } 
                                        <div class="d-flex justify-content-start justify-content-md-end mb-0">
                                          <span>${item.isDiscountedBundle ? 'Bundle Offer' : 'Price'}:&nbsp;</span>
                                          <span class="fw-semibold fs-large">
                                            ${getPriceWithCurrency(order.currencyCode, item.price * item.itemQuantity)}
                                          </span>
                                        </div>
                                        <div class="d-flex justify-content-start justify-content-md-end fs-small">
                                          ${item.freeTrialInfo?.isFreeTrial
              ? html`
                                                <mhecomm-free-trial-subscription-info
                                                  .freeTrial="${item.freeTrialInfo}"
                                                  variant="large"
                                                />
                                              `
              : item.itemQuantity > 1 ? html`
                                                <div>
                                                  <span class="fw-semibold">Item Price:&nbsp;</span
                                                  >${getPriceWithCurrency(order.currencyCode, item.price)}
                                                </div>
                                              `: nothing}
                                        </div>
                                      </div>
                                    </div>
                                    ${!item.freeTrialInfo?.isFreeTrial && !isSharpenPlus(item.programSourceId)
              ? html` <div class="col-4 col-sm-4 col-md-12 col-lg-12">
                                          <div class="d-flex fw-semibold justify-content-end">
                                            Quantity:&nbsp;${item.itemQuantity}
                                          </div>
                                        </div>`
              : nothing}
                                    ${item.amountRefunded > 0
              ? html` <div class="col-4 col-sm-4 col-md-12 col-lg-12">
                                          <div class="d-flex justify-content-end">
                                            <div class="fw-semibold">Return Amount:&nbsp;</div>
                                            ${getPriceWithCurrency(order.currencyCode, item.amountRefunded)}
                                          </div>
                                        </div>`
              : nothing}
                                  </div>
                                </div>
                                <div class="col-12 col-md-3 text-md-end px-2 px-md-0 px-xl-2">
                                  ${this.displayAccessMessageOrLaunchButton(order, item)}
                                  ${this.launchSharpen(order, item)}
                                  <mhecomm-free-trial-cancel
                                    addClass="mt-2"
                                    .product="${item}"
                                    orderNumber="${order.webOrderNo}"
                                    @mhecomm-free-trial-canceled="${() => this.loadOrderHistory()}"
                                  />
                                </div>
                              </div>
                              <hr class="hr-primary m-0" aria-hidden="true" />
                            </li>
                          `
        )}
                      </ul>
                      <div class="row p-3">
                        <div>
                          <button
                            aria-label="View Order Details for ${order.webOrderNo}"
                            class="btn link-primary btn-sm link-collapse px-2 collapsed"
                            data-bs-toggle="collapse"
                            data-bs-target="#orderHistoryCollapse${order.webOrderNo}"
                            aria-expanded="false"
                            aria-controls="orderHistoryCollapse${order.webOrderNo}"
                            @click=${(event) => this._handleCollapseToggle(order, event)}
                            id="orderDetailsLink${order.webOrderNo}"
                          >
                            View Order
                            Details${this.uxtIconReady
            ? html`<uxt-icon
                                  id="icon${order.webOrderNo}"
                                  class="ms-1"
                                  icon="chevron-down-sm"
                                ></uxt-icon>`
            : ''}
                          </button>
                        </div>
                      </div>
                      <div id="orderHistoryCollapse${order.webOrderNo}" class="row p-3 pt-0 collapse">
                        <h4 class="fw-bold fs-6 mb-3">Address/Billing information:</h4>
                        ${order.shippingAddress && this._displayShippingAddress(order, this.user)
            ? html` <div class="col-12 col-sm-4">
                              <h5 class="fw-bold fs-6 mb-0">Shipped To:</h5>
                              <div class="mt-2">
                                ${order.paypalTransactionId
                ? order.shippingAddress.nickName
                : `${order.shippingAddress?.firstName} ${order.shippingAddress?.lastName}`}
                              </div>
                              <div>${order.shippingAddress?.street1}</div>
                              <div>
                                ${order.shippingAddress?.city}, ${order.shippingAddress?.state}
                                ${order.shippingAddress?.zipCode}
                              </div>
                              ${order.shippingAddress.phone
                ? html`<div>
                                    Phone: ${order.shippingAddress?.phone}
                                  </div>`
                : nothing}
                            </div>`
            : nothing}
                        ${order.billingAddress && this._displayBillingAddress(order, this.user)
            ? html`<div class="col-12 ${order.shippingAddress ? 'col-sm-4 mt-4' : 'col-sm-6'} mt-sm-0">
                              <h5 class="fw-bold fs-6 mb-0">Billed To:</h5>
                              <div class="mt-2">
                                ${order.billingAddress?.firstName} ${order.billingAddress?.lastName}
                              </div>
                              <div>${order.billingAddress?.street1}</div>
                              <div>
                                ${order.billingAddress?.city}, ${order.billingAddress?.state}
                                ${order.billingAddress?.zipCode}
                              </div>
                              <div>Phone: <span>${order.billingAddress?.phone}</span></div>
                            </div>`
            : nothing}
                        <div class="col-12 ${order.shippingAddress ? 'col-sm-4' : 'col-sm-6'} mt-4 mt-sm-0">
                          <h5 class="fw-bold fs-6 mb-0">Billing Info:</h5>
                          <div class="d-flex flex-column mt-1 lh-lg">
                            <div class="row">
                              <div class="col">Payment:</div>
                              <div class="col text-end">
                                ${getPaymentMethod(order)}
                                ${order.paymentType === 'Paypal'
            ? html`<div>
                                      ${order.paypalTransactionId}
                                    </div>`
            : nothing}
                                ${order.paymentType === 'Purchase Order'
            ? html`<div>
                                      ${order.purchaseOrderNumber}
                                    </div>`
            : nothing}
                              </div>
                            </div>
                            <div class="row">
                              <div class="col">Subtotal:</div>
                              <div class="col text-end">
                                ${getPriceWithCurrency(order.currencyCode, order.subTotal)}
                              </div>
                            </div>
                            ${order.bundleDiscount
            ? html`
                              <div class="row">
                                <div class="col">Bundle Discount:</div>
                                <div class="col text-end">
                                  - ${getPriceWithCurrency(order.currencyCode, order.bundleDiscount)}
                                </div>
                              </div>`
            : nothing
          }
                            <div class="row">
                              <div class="col">Taxes:</div>
                              <div class="col text-end">
                                ${getPriceWithCurrency(order.currencyCode, order.tax)}
                              </div>
                            </div>
                            ${order.digitalHandlingFees
            ? html`
                                  <div class="row">
                                    <div class="col">Service Fee:</div>
                                    <div class="col text-end">
                                      ${getPriceWithCurrency(order.currencyCode, order.digitalHandlingFees)}
                                    </div>
                                  </div>
                                `
            : nothing}
                            ${order.shippingAddress || order.freightCharges > 0
            ? html`<div class="row">
                                  <div class="col">Shipping:</div>
                                  <div class="col text-end">
                                    ${getPriceWithCurrency(order.currencyCode, order.freightCharges)}
                                  </div>
                                </div>`
            : ''}
                            ${order.totalPromoDiscount && order.totalPromoDiscount > 0
            ? html`
                                  <div class="row">
                                    <div class="col-8">
                                      Promo Code(s) Applied:
                                    </div>
                                    <div class="col text-end">
                                      - ${getPriceWithCurrency(order.currencyCode, order.totalPromoDiscount)}
                                    </div>
                                  </div>
                                `
            : nothing}
                            ${order.bundleDiscount || (this._hasPromoCode(order)) ? html`
                                <div class="row">
                                <div class="col">Total Before Discount:</div>
                                <div class="col fw-bold text-end text-decoration-line-through">
                                  ${getPriceWithCurrency(order.currencyCode, this._totalBeforeDiscount(order))}
                                </div>
                              </div>`: nothing
          }
                            <div class="row fw-bold">
                              <div class="col">Total:</div>
                              <div class="col text-end">
                                ${getPriceWithCurrency(order.currencyCode, order.totalPrice)}
                              </div>
                            </div>
                            ${order.totalRefunded > 0 ? html`
                              <div class="row fw-bold">
                                <div class="col">Total Return Amount:</div>
                                <div class="col text-end">
                                  ${getPriceWithCurrency(order.currencyCode, order.totalRefunded)}
                                </div>
                              </div>
                            ` : nothing}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                `
      )}
            </div>
          </div>
        `;
  }

  getTitle(item: UserOrderDetails) {
    return `
       ${item.productDescription}
       ${!isSharpenPlus(item.programSourceId) && item.edition && parseInt(item.edition) > 0 && !item.isEvergreen
        ? `, ${ordinalSuffixOf(item.edition)} Edition`
        : ''}
       ${appendedDurationForHEProducts({
          businessUnit: item?.businessUnit!,
          isVirtual: item.virtual,
          formatTypeDesc: item?.formatTypeDescription!,
          duration: item?.duration!,
          durationPeriod: item?.durationPeriod!,
          ...(item?.offerType && {offerType: item?.offerType}),
        })
       }
       ${item.isConnectSharpenBundle ? ' (Connect with Sharpen)' : ''}
       ${getSharpenCompanionTitle(item)}
       ${!item.duration && !item.durationPeriod && item.terms
        ? appendDurationForTunnelPurchaseFlow({
            term: item.terms,
          })
        : ''
       }
     `;
  }

  _renderLoadingTemplate() {
    return html`
      <span class="visually-hidden">Loading order history</span>
      ${loading({ width: '100%', height: '5rem', m: '' })}
      <hr aria-hidden="true" />
      <div class="loading-container mb-4">
        ${loading({ width: '100%', height: '12rem' })}
        <div>
          ${loading({ height: '1.25rem', width: '70%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '40%' })} ${loading({ height: '1.25rem', width: '20%' })}
        </div>
        <div>
          ${loading({ height: '1.25rem', width: '100%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '50%' })}
        </div>
      </div>
    `;
  }

  private displayAccessMessageOrLaunchButton(order: IOrderHistory, item: UserOrderDetails) {
    const accessType = getAccessType(item);
    return accessType ? renderAccessMessage(accessType) : this._getButtonFromUserOrderDetails(order, item);
  }

  _getButtonFromUserOrderDetails(order: IOrderHistory, userOrderDetails: UserOrderDetails) {
    if (userOrderDetails.freeTrialInfo && isInactiveFreeTrial(userOrderDetails.freeTrialInfo)) {
      return nothing;
    }

    if (OfferTypeChecker.isBoardsBeyond(userOrderDetails.offerType)) {
      return boardsBeyondCTA(order, userOrderDetails, this._triggerCTAAnalyticsData.bind(this));
    }

    if (OfferTypeChecker.isClinicalReasoning(userOrderDetails.offerType)) {
      return getClinicalReasoningLaunchCta(order, userOrderDetails, this._triggerCTAAnalyticsData.bind(this));
    }

    if (this._isStandAloneTunnelFlow(userOrderDetails)) {
      const btnName = 'Access Now';
      const hrefGenerated = getItemLaunchObjectUrl(userOrderDetails);
      return html`
        <div class="d-flex justify-content-end">
          <div class="col-12 col-lg-9">
            <a
              class="btn btn-outline-primary w-100"
              aria-label="${ariaLabelWithNewTab('Tunnel purchase')}"
              target="_blank"
              data-automation-id="btn-accessnow-${order.webOrderNo}-${userOrderDetails.isbn10}"
              @click=${() =>
                this._triggerCTAAnalyticsData('go-to-tunnel-flow', btnName, order, userOrderDetails, hrefGenerated)}
              href=${hrefGenerated}
            >
              ${btnName}&nbsp;
              <uxt-icon icon="box-arrow-up-right"></uxt-icon>
            </a>
          </div>
        </div>`;
    }

    if (this._isTrackOrder(userOrderDetails)) {
      const btnName = 'Track order';
      const hrefGenerated = `${environment().customerServiceUrl}/order-status`;
      return html`
        <div class="d-flex justify-content-end">
          <div class="col-12 col-lg-9">
            <a
              class="btn btn-outline-primary w-100"
              aria-label="${ariaLabelWithNewTab(`Track Order ${order.webOrderNo}`)}"
              @click=${() => this._triggerCTAAnalyticsData('track-order', btnName, order, userOrderDetails, hrefGenerated)}
              href=${hrefGenerated}
              target="_blank"
              data-automation-id="btn-trackorder-${order.webOrderNo}-${userOrderDetails.isbn10}"
            >
              ${btnName}&nbsp;
              <uxt-icon icon="box-arrow-up-right"></uxt-icon>
            </a>
          </div>
        </div>
      `;
    }

    if (this._isBookshelf(userOrderDetails)) {
      return getMyBookshelfLaunchCta(order, userOrderDetails, this._triggerCTAAnalyticsData.bind(this));
    }

    if (this._isLaunchButton(userOrderDetails)) {
      if (isSharpenPlus(userOrderDetails.programSourceId)) {
        return html`
          <div class="d-flex justify-content-end">
            <div class="col-12 col-lg-9">
              <button
                type="button"
                class="btn btn-outline-primary w-100"
                @click=${() => this.gotosharpen(order.userOrderDetails)}
                aria-label="${ariaLabelWithNewTab('Launch Sharpen')}"
                id="gotosharpen"
                data-automation-id="${`btn-launch-sharpen-${order.webOrderNo}-${userOrderDetails.isbn10}`}"
              >
                Launch Sharpen
                <uxt-icon icon="box-arrow-up-right" class="ms-1"></uxt-icon>
              </button>
            </div>
          </div>
        `;
      } else {
        const isSharpenBundle = isSharpenBundleOrder(userOrderDetails);
        const isSharpenUpsellAvailable = isSharpenUpsellPresentInOrder(order.userOrderDetails);
        const buttonStyle = isSharpenBundle || isSharpenUpsellAvailable ?  'btn-outline-primary w-100' : 'btn-primary w-100';
        const btnName = isSharpenBundle || isSharpenUpsellAvailable ? 'Launch Connect' : 'Launch';
        return html` <div class="d-flex justify-content-end">
          <div class="col-12 col-lg-9">
            <mhecomm-connect-launch-modal
              launchLabel=${btnName}
              buttonstyle=${buttonStyle}
              icon="box-arrow-up-right"
              isbn=${userOrderDetails.isbn10}
              orderId=${order.webOrderNo}
              .connectLaunchUrl=${userOrderDetails.launchObject?.url}
              @opened-modal=${() => this._triggerCTAAnalyticsData('launch-connect', btnName, order, userOrderDetails)}
            >
            </mhecomm-connect-launch-modal>
          </div>
        </div>`;
      }
    }

    if (this._isFirstAidForward(userOrderDetails)) {
      const btnName = 'Launch';
      const hrefGenerated = getFa4AppUrl(userOrderDetails.programSourceId!);
      return html`
        <div class="d-flex justify-content-end">
          <div class="col-12 col-lg-9">
            <a
              class="btn btn-outline-primary w-100"
              aria-label="${ariaLabelWithNewTab('First Aid Forward App')}"
              target="_blank"
              data-automation-id="btn-launch-${order.webOrderNo}-${userOrderDetails.isbn10}"
              @click=${() =>
                this._triggerCTAAnalyticsData('go-to-first-aid-forward', btnName, order, userOrderDetails, hrefGenerated)}
              href=${hrefGenerated}
            >
              ${btnName}&nbsp;
              <uxt-icon icon="box-arrow-up-right"></uxt-icon>
            </a>
          </div>
        </div>`;
    }

    if (this._isAccessCode(userOrderDetails)) {
      return html` <div class="d-flex d-md-block">
        <div class="fw-bold">
          Your Access Code:&nbsp;
        </div>
        <div class="fw-bold">
          ${userOrderDetails.accessCode}
        </div>
      </div>`;
    }

    if (isMhpEbook(userOrderDetails)) {
      return html`
        <mhecomm-mhp-ebook-download
          isbn="${userOrderDetails.isbn10}"
          .eBookUrls="${userOrderDetails.extension_attributes?.ebooks_urls ?? []}"
          instructionsContainerClass="text-md-start"
        ></mhecomm-mhp-ebook-download>
      `;
    }

    return '';
  }

  launchSharpen(order: any, userOrderDetails) {
    if (!isSharpenBundleOrder(userOrderDetails)) {
      return nothing;
    }

    const hrefGenerated = getSharpenBundleLaunchURL(userOrderDetails);
    return html`
      <div class="d-flex justify-content-end mt-2">
        <div class="col-12 col-lg-9">
          <a
            class="btn btn-outline-primary w-100"
            aria-label="${ariaLabelWithNewTab('Launch Sharpen')}"
            @click=${() =>
            this._triggerCTAAnalyticsData('go-to-sharpen', 'Launch Sharpen', order, userOrderDetails, hrefGenerated)}
            href=${hrefGenerated}
            target="_blank"
            data-automation-id="btn-launch-sharpen-${order.webOrderNo}-${userOrderDetails.isbn10}"
          >
            Launch Sharpen&nbsp;
            <uxt-icon icon="box-arrow-up-right"></uxt-icon>
          </a>
        </div>
      </div>
    `;
  }

  _isTrackOrder(orderDetail: UserOrderDetails) {
    return orderDetail.productType === ProductType.SHIPPABLE;
  }

  _isEbook(orderDetail: UserOrderDetails): boolean {
    const branding = orderDetail.productBrandingCode;
    return branding === eBookRentalBranding || branding === eBookPurchaseBranding;
  }

  _isBookshelf(orderDetail: UserOrderDetails) {
    return (
      isHEOrder(orderDetail) &&
      orderDetail.virtual &&
      (orderDetail.source === productSourceCreate || this._isEbook(orderDetail))
    );
  }

  _isLaunchButton(orderDetail: UserOrderDetails) {
    return (
      isHEOrder(orderDetail) &&
      orderDetail.virtual &&
      !orderDetail.displayCode &&
      productSourceCreate !== orderDetail.source &&
      !this._isEbook(orderDetail) &&
      !OfferTypeChecker.isGdp(orderDetail?.offerType)
    );
  }

  _isFirstAidForward(orderDetail: UserOrderDetails) {
    return orderDetail.productSegment === PROFESSIONAL && OfferTypeChecker.isFirstAidForward(orderDetail?.offerType);
  }

  _isStandAloneTunnelFlow(orderDetail: UserOrderDetails) {
    switch (orderDetail.itemType) {
      case ItemType.ltia:
      case ItemType.apr:
      case ItemType.nutricalc:
      case ItemType.simnet:
        return true;
      default:
        return false;
    }
  }

  _isAccessCode(orderDetail: UserOrderDetails) {
    return orderDetail.virtual && orderDetail.displayCode;
  }

  _isPhysicalProduct(order: IOrderHistory) {
    return order.userOrderDetails.some((orderDetail: UserOrderDetails) => orderDetail.virtual === false);
  }

  _displayBillingAddress(order: IOrderHistory, user: UserProfile | undefined) {
    if (!user) return true;
    let isPhysical = false;
    isPhysical = this._isPhysicalProduct(order);

    // If this is an instructor and there is no physical products, do not display billing
    if (isHEInstructor(user) && !isPhysical) return false;

    return true;
  }

  _displayShippingAddress(order: IOrderHistory, user: UserProfile | undefined) {
    if (!user) return true;
    let isPhysical = false;
    isPhysical = this._isPhysicalProduct(order);

    // If there are no physical products, do not display shipping
    if (!isPhysical) return false;

    return true;
  }

  _triggerCTAAnalyticsData(name: string, label: string, order: IOrderHistory, item: UserOrderDetails, href?: string) {
    const orderDetail: any = {
      orderNumber: order.webOrderNo,
      userRole: this.user?.persona.role,
      userSegment: this.user?.persona.segment,
      orderStatus: order.orderStatusText,
      productStatus: getOrderUiStatus(order, item),
      ...getOrderTypeFromOrderDetails(order),
      buttonName: label,
      isbn13: item.isbn13,
      mhid: item.mhid,
      itemType: item.itemType,
      editionOrRelease: item.editionOrRelease,
      ...(href && { href: href }),
      ...(getUserActiveTab() !== '' && { myAccountSection: getUserActiveTab() }),
    };

    if (item.productSegment.toLowerCase() === 'he') {
      item.programSourceId;
    }

    if (item.productSegment.toLowerCase() === 'he') {
      orderDetail.programSourceId = item.programSourceId;
      orderDetail.programSourceName = item.programSourceName;
      orderDetail.isEvergreenPDP = item.isEvergreen;
    }

    emit({
      element: this,
      name,
      detail: JSON.parse(JSON.stringify(orderDetail)),
    });
  }

  _denormalizeOrderHistory(orders: IOrderHistory[]): IOrderHistory[] {
    const newo = [...orders];
    return newo
      .filter((order) => !order.webOrderNo.startsWith('CODE_'))
      .filter((order) => order.orderType?.toUpperCase() !== 'INSTRUCTOR_COMP')
      .map((order) => {
        const { ...denormalized } = order;
        denormalized.billingAddress = order.address.find((address) => address.type === AddressType.BILLING_ADDRESS);
        denormalized.shippingAddress = order.address.find((address) => address.type === AddressType.SHIPPING_ADDRESS);
        return denormalized;
      });
  }

  _findAddressByType(order: IOrderHistory, addressType: AddressType): AddressBook[] {
    return order.address.filter((address) => address.type === addressType);
  }

  _handleCollapseToggle(order, event) {
    if (event && (event.type == 'click' || (event.type == 'keyup' && event.code == 'Space'))) {
      this.collapses[event.currentTarget.dataset.bsTarget].toggle();
      const orderDetails = this.shadowRoot!.querySelector(`#${event.currentTarget.id}`) as HTMLLinkElement;
      const iconCollapse = this.shadowRoot!.querySelector(`#icon${order.webOrderNo}`) as HTMLLinkElement;
      if (orderDetails.getAttribute('aria-expanded') == 'true') {
        orderDetails.setAttribute('aria-expanded', 'false');
        iconCollapse.setAttribute('icon', 'chevron-down-sm');
      } else {
        orderDetails.setAttribute('aria-expanded', 'true');
        iconCollapse.setAttribute('icon', 'chevron-up-sm');
      }
      orderDetails.classList.toggle('collapsed');
      this._viewOrderDetailsAnalyticsData(order);
    }
  }

  _isLoading() {
    if (window.Mhecomm_Editor) {
      return false;
    }

    return this.loading;
  }

  async _orderHistoryAnalyticsData(orders: IOrderHistory[]) {
    emit({
      element: this,
      name: 'order-history-load',
      detail: {
        appFlow: getAppFlow(),
        user: getAnalyticsSafeData(this.user!, true),
        orders: orders.map((order) => this._getSafeOrderData(order)),
        totalOrders: orders.length,
        myAccountSection: getUserActiveTab(),
      },
    });
  }

  _viewOrderDetailsAnalyticsData(order: IOrderHistory) {
    emit({
      element: this,
      name: 'view-order-details',
      detail: {
        appFlow: getAppFlow(),
        user: getAnalyticsSafeData(this.user!, true),
        orderNumber: order.webOrderNo,
        orderedDate: order.orderedDate.substring(0, 10),
        overAllOrderStatus: order.overAllOrderStatus.toString(),
        subTotal: parseFloat(order.subTotal ? order.subTotal.toFixed(2) : "0"),
        tax: parseFloat(order.tax ? order.tax.toFixed(2) : "0"),
        bundleDiscount: parseFloat(order.bundleDiscount ? order.bundleDiscount.toFixed(2) : "0"),
        shipping: parseFloat(order.freightCharges ? order.freightCharges.toFixed(2) : "0"),
        promocodeDiscount: parseFloat(order.totalPromoDiscount ? order.totalPromoDiscount.toFixed(2) : "0"),
        totalPrice: parseFloat(order.totalPrice ? order.totalPrice.toFixed(2) : "0"),
        isEdoms: !!order.metaOfferId,
        myAccountSection: getUserActiveTab(),
        ...getOrderTypeFromOrderDetails(order),
        ...getProductTypeBU({ programIds: getOrderProgramIds(order) }),
        ...this._getReturnInfo(order)
      },
    });
  }

  _getReturnInfo(order: IOrderHistory) {
    return {
      returnStatus: order.returnInfo?.status,
      returnTotal: order.totalRefunded,
      returnCompletedDate: order.returnInfo?.dateCompleted
    }
  }

  _getSafeOrderData(order: IOrderHistory) {
    return {
      webOrderNo: order.webOrderNo,
      totalPrice: order.totalPrice,
      subTotal: parseFloat(order.subTotal ? order.subTotal.toFixed(2) : "0"),
      tax: parseFloat(order.tax ? order.tax.toFixed(2) : "0"),
      bundleDiscount: parseFloat(order.bundleDiscount ? order.bundleDiscount.toFixed(2) : "0"),
      shipping: parseFloat(order.freightCharges ? order.freightCharges.toFixed(2) : "0"),
      promocodeDiscount: parseFloat(order.totalPromoDiscount ? order.totalPromoDiscount.toFixed(2) : "0"),
      orderedDate: order.orderedDate.substring(0, 10),
      isEdoms: !!order.metaOfferId,
      overAllOrderStatus: order.overAllOrderStatus.toString(),
      paymentType: determinePaymentType(order),
      totalProductCount: order.totalProductCount,
      ...getOrderTypeFromOrderDetails(order),
      ...getProductTypeBU({ programIds: getOrderProgramIds(order) }),
      ...(order.userOrderDetails.some((product) => product.programSourceId === SHARPEN_PROGRAM_ID) &&
        getSharpenAnalyticsOrderDetails(order)),
      ...this._getReturnInfo(order),
      ...(order.userOrderDetails.some((product) => product?.extension_attributes?.business_unit === PROFESSIONAL) && {
        businessUnit: PROFESSIONAL,
      }),
    };
  }

  public gotosharpen(userOrderDetailsList: UserOrderDetails[]) {
    emit({
      element: this,
      name: 'mhecomm-go-to-sharpen',
      dataLayer: true,
      detail: { id: this.id },
    });
    window.open(getLaunchSharpenUrl(userOrderDetailsList));
  }

  _totalBeforeDiscount(order: IOrderHistory) {
    return order.subTotal + order.digitalHandlingFees + order.freightCharges + order.tax;
  }

  _hasPromoCode(order: IOrderHistory) {
    return order.totalPromoDiscount && order.totalPromoDiscount > 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-order-history': OrderHistory;
  }
}
